import React, { useEffect, useRef } from 'react';
import { Card, Statistic } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const CostDistributionChart = ({ data, title = "Cost Distribution" }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    // Clean up any existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // If no data is available, don't create the chart
    if (!data || !Array.isArray(data) || data.length === 0) {
      return;
    }

    // Format data for the chart
    const labels = data.map(item => item.name);
    const values = data.map(item => item.value);
    const colors = data.map(item => item.color || `#${Math.floor(Math.random()*16777215).toString(16)}`);

    // Create the chart
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderColor: colors.map(() => '#ffffff'),
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: 'right',
            labels: {
              boxWidth: 12,
              font: {
                size: 11
              }
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.raw || 0;
                const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return `${label}: $${value.toLocaleString()} (${percentage}%)`;
              }
            }
          },
          datalabels: {
            formatter: (value, ctx) => {
              const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
              const percentage = Math.round((value / sum) * 100);
              return percentage > 5 ? `${percentage}%` : '';
            },
            color: '#ffffff',
            font: {
              weight: 'bold',
              size: 11
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  // Calculate total cost
  const totalCost = data && Array.isArray(data) 
    ? data.reduce((sum, item) => sum + (item.value || 0), 0)
    : 0;

  // Handle case when data is not available
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card title={title} className="dashboard-card">
        <div className="card-content">
          <p>No cost data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <div className="card-title">
          <DollarOutlined /> {title}
        </div>
      } 
      className="dashboard-card"
      extra={
        <Statistic 
          title="Total Cost" 
          value={totalCost} 
          precision={2}
          prefix="$"
          valueStyle={{ fontSize: '16px', color: '#1890ff' }}
        />
      }
    >
      <div className="card-content" style={{ height: 300 }}>
        <canvas ref={chartRef} />
      </div>
    </Card>
  );
};

export default CostDistributionChart;
